import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, forkJoin, map, of, catchError } from 'rxjs';
import { RequestService } from './request.service';
import { ContestsService } from '../../Contests/services/contests.service';
import { SubscriptionService } from '../../subscription/service/subscription.service';

/** Open ("new") requests per Requests-page tab. Keys match RequestComponent.requestTabs. */
export interface RequestCounts {
  contact: number;
  partner: number;
  contest: number;
  enterprise: number;
  cashApproval: number;
  sdkCash: number;
  pendingOnline: number;
}

const EMPTY_COUNTS: RequestCounts = {
  contact: 0, partner: 0, contest: 0, enterprise: 0, cashApproval: 0, sdkCash: 0, pendingOnline: 0,
};

/**
 * Shared source for the sidebar "Request" badge (sum of all tabs) and the per-tab badges on the
 * Requests page, so both always show the same numbers. "New" means the status a request starts in:
 * `new` for Contact / Partner / Enterprise, `pending` for Campaign / Cash / SDK / Online.
 */
@Injectable({ providedIn: 'root' })
export class RequestCountService {
  private readonly countsSubject = new BehaviorSubject<RequestCounts>({ ...EMPTY_COUNTS });
  readonly counts$ = this.countsSubject.asObservable();
  readonly total$ = this.counts$.pipe(
    map((c) => Object.values(c).reduce((sum, n) => sum + (Number(n) || 0), 0))
  );

  private inFlight?: Subscription;

  constructor(
    private requestService: RequestService,
    private contestsService: ContestsService,
    private subscriptionService: SubscriptionService,
  ) {}

  /** Re-fetch every tab's count (silently — no global loader). */
  refresh(): void {
    this.inFlight?.unsubscribe();
    const one = { page: 1, per_page: 1 };
    this.inFlight = forkJoin({
      contact: this.total(this.requestService.getRequests({ ...one, status: 'new' }, true)),
      partner: this.total(this.requestService.getPartnerRequests({ ...one, status: 'new' }, true)),
      contest: this.total(this.contestsService.getContestRequestsList({ ...one, status: 'pending' }, true)),
      enterprise: this.total(this.subscriptionService.getEnterprisePlans({ ...one, request_status: 'new' }, true)),
      sdkCash: this.total(this.requestService.getSdkCashRequests({ ...one, status: 'pending' }, true)),
      // 8099 / 8165 ignore a status filter, so these two are counted from the full list.
      cashApproval: this.countWhere(this.requestService.getCashSubscriptions({}, true), 'pending'),
      pendingOnline: this.countWhere(
        this.requestService.getPendingOnlinePayments({ page: 1, per_page: 1000 }, true), 'pending'
      ),
    }).subscribe((counts) => this.countsSubject.next(counts));
  }

  private total(source: Observable<any>): Observable<number> {
    return source.pipe(
      map((res: any) => {
        if (typeof res?.itemsTotal === 'number') return res.itemsTotal;
        if (typeof res?.totalItems === 'number') return res.totalItems;
        if (typeof res?.total === 'number') return res.total;
        if (Array.isArray(res?.items)) return res.items.length;
        return Array.isArray(res) ? res.length : 0;
      }),
      catchError(() => of(0))
    );
  }

  private countWhere(source: Observable<any>, status: string): Observable<number> {
    return source.pipe(
      map((res: any) => {
        const items: any[] = Array.isArray(res) ? res : res?.items ?? [];
        return items.filter((i) => String(i?.status || '').toLowerCase() === status).length;
      }),
      catchError(() => of(0))
    );
  }
}
